import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CODE_LENGTH, CodeBox } from './CodeBox';
import { RegisterHeader } from './RegisterHeader';

export function ConfirmAccountForm({ onSubmit, isActive, error, onClearError }: {
  onSubmit: (code: string) => Promise<void>;
  isActive: boolean;
  error: string | null;
  onClearError: () => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = useCallback(async (fullCode: string) => {
    setIsLoading(true);
    try {
      await onSubmit(fullCode);
    } finally {
      setIsLoading(false);
    }
  }, [onSubmit]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (isLoading) return;

    const key = e.key;

    if (key === 'Backspace') {
      e.preventDefault();
      setCode(prev => {
        const newCode = [...prev];
        if (newCode[focusedIndex] === '') {
          // If current box is empty, move to previous and clear it
          if (focusedIndex > 0) {
            newCode[focusedIndex - 1] = '';
            setFocusedIndex(focusedIndex - 1);
          }
        } else {
          // Clear current box
          newCode[focusedIndex] = '';
        }
        return newCode;
      });
      return;
    }

    if (key === 'ArrowLeft') {
      e.preventDefault();
      setFocusedIndex(prev => Math.max(0, prev - 1));
      return;
    }

    if (key === 'ArrowRight') {
      e.preventDefault();
      setFocusedIndex(prev => Math.min(CODE_LENGTH - 1, prev + 1));
      return;
    }

    // Only allow single alphanumeric characters
    if (key.length === 1 && /^[a-zA-Z0-9]$/.test(key)) {
      e.preventDefault();
      onClearError();
      setCode(prev => {
        const newCode = [...prev];
        newCode[focusedIndex] = key.toUpperCase();

        // Check if this is the last character
        if (focusedIndex === CODE_LENGTH - 1) {
          const fullCode = newCode.join('');
          if (fullCode.length === CODE_LENGTH) {
            handleSubmit(fullCode);
          }
        } else {
          // Move to next box
          setFocusedIndex(focusedIndex + 1);
        }

        return newCode;
      });
    }
  }, [focusedIndex, isLoading, handleSubmit]);

  useEffect(() => {
    if (!isActive) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, isActive]);

  return (
    <div className="w-full">
      <RegisterHeader
        title={t('auth.confirmAccount', 'Confirm your account')}
        subtitle={t('auth.confirmAccountSubtitle', 'Enter the 6-digit code sent to your email')} />

      <div className="flex justify-center gap-2 sm:gap-3 mb-6">
        {code.map((digit, index) => (
          <CodeBox
            key={index}
            value={digit}
            isFocused={focusedIndex === index && !isLoading}
            isDisabled={isLoading}
            onClick={() => setFocusedIndex(index)} />
        ))}
      </div>

      <p className="text-center text-sm text-muted mb-4">
        {t('auth.confirmAccountNote', 'If you do not see the email, please check your spam folder. Delivery may take up to 1 minute.')}
      </p>

      {isLoading && (
        <div className="flex justify-center items-center gap-2 text-muted">
          <span className="animate-spin inline-block size-5 border-2 border-current border-t-transparent rounded-full" />
          <span className="text-sm">{t('auth.verifying', 'Verifying...')}</span>
        </div>
      )}

      {error && (
        <div className="p-3 rounded-m bg-error/10 border border-error/20">
          <p className="text-sm text-error text-center">{error}</p>
        </div>
      )}

      <p className="text-center text-sm text-muted mt-4">
        {t('auth.didntReceiveCode', "Didn't receive the code?")}{' '}
        <button
          type="button"
          disabled={isLoading}
          className="text-primary hover:text-primary/80 font-medium transition-colors disabled:opacity-50 disabled:pointer-events-none"
        >
          {t('auth.resendCode', 'Resend')}
        </button>
      </p>
    </div>
  );
}
